// src/components/brand/MRNLogo.tsx

import React from "react";

// ── Design tokens ─────────────────────────────────────────────
const NAVY  = "#1E2761";
const WHITE = "#FFFFFF";
const CYAN  = "#0891B2";

// ── The Mark — navy square with white M and cyan accent dot ───
function Mark({ size = 48 }: { size?: number }) {
  const r  = 4 * (size / 48);          // border-radius scaled
  const fs = size * 0.52;              // M font size
  const dotR  = size * 0.095;          // dot radius
  const dotCx = size * 0.685;          // dot center x
  const dotCy = size * 0.685;          // dot center y

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="MRN mark"
    >
      {/* Navy square */}
      <rect width={size} height={size} rx={r} fill={NAVY} />

      {/* White M — centered, weight 900 */}
      <text
        x={size * 0.5}
        y={size * 0.72}
        textAnchor="middle"
        fill={WHITE}
        fontFamily="Inter, system-ui, sans-serif"
        fontWeight="900"
        fontSize={fs}
        letterSpacing="-0.04em"
      >
        M
      </text>

      {/* Cyan accent dot — bottom-right of M letterform */}
      <circle cx={dotCx} cy={dotCy} r={dotR} fill={CYAN} />
    </svg>
  );
}

// ── Variant props ─────────────────────────────────────────────
export type LogoVariant = "mark" | "sidebar" | "full" | "horizontal";

interface MRNLogoProps {
  variant?: LogoVariant;
  size?:    number;         // mark size (default 48)
  onDark?:  boolean;        // true = white text, false = navy text
}

// ── Main component ────────────────────────────────────────────
export default function MRNLogo({
  variant  = "mark",
  size     = 48,
  onDark   = true,
}: MRNLogoProps) {
  const textColor    = onDark ? WHITE : NAVY;
  const markSize     = variant === "sidebar"    ? 32
                     : variant === "horizontal" ? 40
                     : variant === "full"       ? size
                     : size;

  // ── mark only ───────────────────────────────────────────────
  if (variant === "mark") {
    return <Mark size={markSize} />;
  }

  // ── sidebar: mark + MRN + Retail Intelligence ────────────────
  if (variant === "sidebar") {
    return (
      <div className="flex items-center gap-2.5" aria-label="MRN Retail Intelligence">
        <Mark size={markSize} />
        <div className="flex flex-col leading-none">
          <span
            style={{
              fontFamily:    "Inter, system-ui, sans-serif",
              fontWeight:    800,
              fontSize:      15,
              letterSpacing: "0.04em",
              color:         textColor,
              lineHeight:    1,
            }}
          >
            MRN
          </span>
          <span
            style={{
              fontFamily:    "Inter, system-ui, sans-serif",
              fontWeight:    500,
              fontSize:      10,
              letterSpacing: "0.06em",
              color:         CYAN,
              lineHeight:    1,
              marginTop:     3,
              textTransform: "uppercase" as const,
            }}
          >
            Retail Intelligence
          </span>
        </div>
      </div>
    );
  }

  // ── full: mark + stacked McCLATCHY / RETAIL / NETWORK ────────
  if (variant === "full") {
    return (
      <div className="flex flex-col items-center gap-3" aria-label="McClatchy Retail Network">
        <Mark size={markSize} />
        <div className="flex flex-col items-center leading-none gap-0.5">
          {["McCLATCHY", "RETAIL"].map((word) => (
            <span
              key={word}
              style={{
                fontFamily:    "Inter, system-ui, sans-serif",
                fontWeight:    800,
                fontSize:      markSize * 0.28,
                letterSpacing: "0.1em",
                color:         textColor,
                lineHeight:    1.2,
                textTransform: "uppercase" as const,
              }}
            >
              {word}
            </span>
          ))}
          <span
            style={{
              fontFamily:    "Inter, system-ui, sans-serif",
              fontWeight:    800,
              fontSize:      markSize * 0.28,
              letterSpacing: "0.1em",
              color:         CYAN,
              lineHeight:    1.2,
              textTransform: "uppercase" as const,
            }}
          >
            NETWORK
          </span>
        </div>
      </div>
    );
  }

  // ── horizontal: mark + McCLATCHY RETAIL / NETWORK ────────────
  if (variant === "horizontal") {
    return (
      <div className="flex items-center gap-3" aria-label="McClatchy Retail Network">
        <Mark size={markSize} />
        <div className="flex flex-col leading-none">
          <span
            style={{
              fontFamily:    "Inter, system-ui, sans-serif",
              fontWeight:    800,
              fontSize:      markSize * 0.28,
              letterSpacing: "0.1em",
              color:         textColor,
              lineHeight:    1.2,
              textTransform: "uppercase" as const,
            }}
          >
            McCLATCHY RETAIL
          </span>
          <span
            style={{
              fontFamily:    "Inter, system-ui, sans-serif",
              fontWeight:    800,
              fontSize:      markSize * 0.28,
              letterSpacing: "0.1em",
              color:         CYAN,
              lineHeight:    1.2,
              textTransform: "uppercase" as const,
            }}
          >
            NETWORK
          </span>
        </div>
      </div>
    );
  }

  return null;
}